import { useMutation } from "@tanstack/react-query";

import {
  DocumentGetResponse,
  DocumentUrlDownloadRequest,
  DocumentUrlDownloadResponse,
  QuestionnaireRecommendListRequest,
  QuestionnaireListResponse,
} from "capitalrx-types";
import { uploadToS3 } from "@/api";

import { Auth, Contact, Finance, Medical, PACase, Pharmacy, PharmacyContracting, ProviderManagement } from "./interfaces";
import { request } from "./request";

export async function authLoginInit(payload: Auth.LoginInitRequest): Promise<Auth.LoginInitResponse> {
  const response = await request<Auth.LoginInitResponse>({
    url: "/auth/login_init",
    method: "POST",
    data: payload,
  });

  return response;
}

export async function authLoginOtp(payload: Auth.LoginOTPRequest): Promise<Auth.LoginOTPResponse> {
  const response = await request<Auth.LoginOTPResponse>({
    url: "/auth/login_otp",
    method: "POST",
    data: payload,
  });

  return response;
}

export async function authLogout(authToken: string) {
  const response = await request(
    {
      url: "/auth/logout",
      method: "POST",
      data: {},
    },
    authToken,
  );

  return response;
}

export async function authRefreshToken(authToken: string, refreshToken: string, username: string): Promise<Auth.LoginTokens> {
  const response = await request<Auth.LoginTokens>(
    {
      url: "/auth/refresh_token",
      method: "POST",
      data: { refresh_token: refreshToken, username: username },
    },
    authToken,
  );

  return response;
}

export async function authGetUser(authToken: string): Promise<Auth.GetUserResponse> {
  const response = await request<Auth.GetUserResponse>(
    {
      url: "/auth/user_get",
      method: "POST",
      data: {},
    },
    authToken,
  );

  return response;
}

export async function authClearRedirect(payload: Auth.ClearRedirectRequest): Promise<Auth.ClearRedirectResponse> {
  const response = await request<Auth.ClearRedirectResponse>({
    url: "/clear_auth/redirect",
    method: "POST",
    data: payload,
  });

  return response;
}

export async function getStatePatient(payload: Auth.StatePatientRequest): Promise<Auth.StatePatientResponse> {
  const response = await request<Auth.StatePatientResponse>({
    url: "/fhir/state_patient_get",
    method: "POST",
    data: payload,
  });

  return response;
}

export async function createProviderManagementAPI(
  payload: ProviderManagement.ProviderCreateRequest,
  authToken: string,
): Promise<ProviderManagement.ProviderCreateResponse> {
  const response = await request<ProviderManagement.ProviderCreateResponse>(
    {
      url: "/providermanagement/create",
      method: "POST",
      data: payload,
    },
    authToken,
  );

  return response;
}

export async function getProviderManagementAPI(authToken: string): Promise<ProviderManagement.ProviderGetResponse> {
  const response = await request<ProviderManagement.ProviderGetResponse>(
    {
      url: "/providermanagement/get",
      method: "POST",
      data: {},
    },
    authToken,
  );

  return response;
}

export async function updateProviderManagementAPI(
  payload: Partial<ProviderManagement.ProviderSchema>,
  authToken: string,
): Promise<ProviderManagement.ProviderUpdateResponse> {
  const response = await request<ProviderManagement.ProviderUpdateResponse>(
    {
      url: "/providermanagement/update",
      method: "POST",
      data: payload,
    },
    authToken,
  );

  return response;
}

export async function updatePaymentPreferenceAPI(
  payload: Partial<ProviderManagement.PaymentPreferenceSchema>,
  authToken: string,
): Promise<ProviderManagement.ProviderUpdateResponse> {
  const response = await request<ProviderManagement.ProviderUpdateResponse>(
    {
      url: "/providermanagement/payment_preference_update",
      method: "POST",
      data: payload,
    },
    authToken,
  );

  return response;
}

export async function createPaymentPreferenceAPI(
  payload: ProviderManagement.PaymentPreferenceCreateRequest,
  authToken: string,
): Promise<ProviderManagement.PaymentPreferenceSchema> {
  const response = await request<ProviderManagement.PaymentPreferenceSchema>(
    {
      url: "/providermanagement/payment_preference_create",
      method: "POST",
      data: payload,
    },
    authToken,
  );

  return response;
}

export async function getPaymentPreferenceAPI(
  payload: Partial<ProviderManagement.PaymentPreferenceGetSchema>,
  authToken: string,
): Promise<ProviderManagement.PaymentPreferenceSchema> {
  const response = await request<ProviderManagement.PaymentPreferenceSchema>(
    {
      url: "/providermanagement/payment_preference_get",
      method: "POST",
      data: payload,
    },
    authToken,
  );

  return response;
}

export async function getPaymentPreferenceListAPI(
  payload: ProviderManagement.PaymentPreferenceListSearchRequest,
  authToken: string,
): Promise<ProviderManagement.PaymentPreferenceListSearchResponse> {
  const response = await request<ProviderManagement.PaymentPreferenceListSearchResponse>(
    {
      url: "/providermanagement/payment_preference_list",
      method: "POST",
      data: payload,
    },
    authToken,
  );

  return response;
}

export async function submitProviderManagementAPI(authToken: string): Promise<ProviderManagement.ProviderUpdateResponse> {
  const response = await request<ProviderManagement.ProviderUpdateResponse>(
    {
      url: "/providermanagement/submit",
      method: "POST",
      data: {},
    },
    authToken,
  );

  return response;
}

export async function getProviderManagementDocumentsUrlAPI(payload: ProviderManagement.ProviderDocumentFileUploadRequest, authToken: string): Promise<ProviderManagement.ProviderDocumentFileUploadResponse> {
  const response = await request<ProviderManagement.ProviderDocumentFileUploadResponse>(
    {
      url: "/providermanagement/documents_url_for_upload",
      method: "POST",
      data: payload,
    },
    authToken,
  );

  return response;
}

export async function getProviderManagementNetworkListAPI(payload: Partial<ProviderManagement.ProviderNetworkListSearchRequest>, authToken: string): Promise<ProviderManagement.ProviderNetworkListSearchResponse> {
  const response = await request<ProviderManagement.ProviderNetworkListSearchResponse>(
    {
      url: "/providermanagement/network_list",
      method: "POST",
      data: payload,
    },
    authToken,
  );

  return response;
}

export async function getProviderManagementNetworkConfigListAPI(payload: Partial<ProviderManagement.ProviderNetworkConfigListSearchRequest>, authToken: string): Promise<ProviderManagement.ProviderNetworkConfigListSearchResponse> {
  const response = await request<ProviderManagement.ProviderNetworkConfigListSearchResponse>(
    {
      url: "/providermanagement/network_config_list",
      method: "POST",
      data: payload,
    },
    authToken,
  );

  return response;
}

export async function getMedicalEligibilityAPI(payload: Medical.MedicalEligibilityRequest): Promise<Medical.MedicalEligibilityResponse> {
  const response = await request<Medical.MedicalEligibilityResponse>(
    {
      url: "/medical_eligibility/get",
      method: "POST",
      data: payload,
    },
  );

  return response;
}

export async function createMedicalClaim(payload: Medical.MedicalBenefitRequest): Promise<Medical.MedicalBenefitResponse> {
  const response = await request<Medical.MedicalBenefitResponse>(
    {
      url: "/medical_test_claim/create",
      method: "POST",
      data: payload,
    },
  );

  return response;
}

export async function getPACaseAPI(payload: PACase.PACaseRequest): Promise<PACase.PACaseResponse> {
  const response = await request<PACase.PACaseResponse>({
    url: "/pa_case/get",
    method: "POST",
    data: payload,
  });

  return response;
}
export async function getPACaseDangerousAPI(payload: { case_id: string, provider_npi: string, member_first_name: string, member_last_name: string }, authToken: string): Promise<PACase.PACaseResponse> {
  const response = await request<PACase.PACaseResponse>({
    url: "/pa_case/get_case_id_only",
    method: "POST",
    data: payload,
  },
    authToken);

  return response;
}
export async function validatePACaseAPI(payload: PACase.PACaseValidateRequest): Promise<PACase.PACaseValidateResponse> {
  const response = await request<PACase.PACaseValidateResponse>({
    url: "/pa_case/validate",
    method: "POST",
    data: payload,
  });

  return response;
}

export async function createPACaseAPI(payload: PACase.PACaseCreateRequest): Promise<PACase.PACaseCreateResponse> {
  const response = await request<PACase.PACaseCreateResponse>({
    url: "/pa_case/create",
    method: "POST",
    data: payload,
  });

  return response;
}

export async function getQuestionnairesAPI(payload: QuestionnaireRecommendListRequest): Promise<QuestionnaireListResponse> {
  const response = await request<QuestionnaireListResponse>({
    url: "/pa_questionnaire/recommend",
    method: "POST",
    data: payload,
  });

  return response;
}

// Formulary
export async function formularyGetByMemberAPI(payload: any): Promise<any> {
  const response = await request<any>({
    url: "/formulary/get_by_member",
    method: "POST",
    data: payload,
  });

  return response;
}

export async function formularyListByIdAPI(payload: any): Promise<any> {
  const response = await request<any>({
    url: "/formulary/list",
    method: "POST",
    data: payload,
  });

  return response;
}


export async function createDocumentAPI(payload: PACase.PADocumentCreateRequest): Promise<DocumentGetResponse> {
  const response = await request<DocumentGetResponse>({
    url: "/pa_document/create",
    method: "POST",
    data: payload,
  });

  return response;
}

export async function attachDocumentAPI(payload: PACase.PADocumentAttachRequest): Promise<{ message: string }> {
  const response = await request<{ message: string }>({
    url: "/pa_document/attach",
    method: "POST",
    data: payload,
  });

  return response;
}

export async function financeGetRemittanceAPI(payload: Pharmacy.FinanceGetRemittanceRequest): Promise<{ message: string }> {
  const response = await request<{ message: string }>({
    url: "/finance_835/get_remittance",
    method: "POST",
    data: payload,
  });

  return response;
}

export async function financeGetMemberPaymentsList(payload: Finance.MemberPaymentsListRequestSchema): Promise<Finance.MedicalMemberPaymentsListResultSchema> {
  const response = await request<Finance.MedicalMemberPaymentsListResultSchema>({
    url: "/finance_835/list_member_payments",
    method: "POST",
    data: payload,
  });

  return response;
}


export async function financeGetProviderPaymentsList(payload: Finance.ProviderPaymentsListRequestSchema, authToken: string): Promise<Finance.MedicalProviderPaymentsListResultSchema> {
  const response = await request<Finance.MedicalProviderPaymentsListResultSchema>({
    url: "/finance_835/list_provider_payments",
    method: "POST",
    data: payload,
  },
    authToken,
  );

  return response;
}

export async function getPACaseList(payload: Partial<PACase.PACaseListRequest>, authToken: string): Promise<PACase.PACaseListResponse> {
  const response = await request<PACase.PACaseListResponse>(
    {
      url: "/pa_case/list",
      method: "POST",
      data: payload,
    },
    authToken,
  );

  return response;
}

export async function getPCDocumentList(payload: Partial<PharmacyContracting.PCDocumentListRequest>, authToken: string): Promise<PharmacyContracting.PCDocumentListResponse> {
  const response = await request<PharmacyContracting.PCDocumentListResponse>(
    {
      url: "/pc_document/list",
      method: "POST",
      data: payload,
    },
    authToken,
  );

  return response;
}

export async function getPCDocumentURLForDownload(payload: DocumentUrlDownloadRequest, authToken: string): Promise<DocumentUrlDownloadResponse> {
  return await request<DocumentUrlDownloadResponse>(
    {
      url: "/pc_document/url_for_download",
      method: "POST",
      data: payload,
    },
    authToken,
  );
}

export async function logPCDocumentDownload(doctype: String, authToken: string): Promise<undefined> {
  await request(
    {
      url: "/structlog/ingest_authed",
      method: "POST",
      data: { log_type: "pc_document_download", log: { doctype } },
    },
    authToken,
  );
}

export async function getContactAPI(authToken: string): Promise<Contact.GetNotificationResponse> {
  const response = await request<Contact.GetNotificationResponse>(
    {
      url: "/contact/get",
      method: "POST",
      data: {},
    },
    authToken,
  );

  return response;
}

export async function updateContactAPI(
  payload: Contact.NotificationRequest,
  authToken: string,
): Promise<Contact.GetNotificationResponse> {
  const response = await request<Contact.GetNotificationResponse>(
    {
      url: "/contact/update",
      method: "POST",
      data: payload,
    },
    authToken,
  );

  return response;
}

export type CreateAndAttachDocumentParams = {
  case_id: number;
  external_member_id: string;
  prescriber_npi: string;
  member_date_of_birth: string;
  file: File[];
};

export const useCreateAndAttachDocument = () => {
  return useMutation({
    mutationFn: async ({
      case_id,
      external_member_id,
      prescriber_npi,
      member_date_of_birth,
      file,
    }: CreateAndAttachDocumentParams) => {
      const createResponse = await createDocumentAPI({
        case_id,
        external_member_id,
        prescriber_npi,
        member_date_of_birth,
        filename: file[0].name,
      });
      if (createResponse.url_for_upload) {
        await uploadToS3(
          createResponse.url_for_upload,
          // @ts-ignore
          file[0].file,
        );

        await attachDocumentAPI({
          case_id,
          external_member_id,
          prescriber_npi,
          member_date_of_birth,
          document_uuids: [createResponse.uuid],
        });
      }

      return createResponse;
    },
  });
};
